import { PushPayloadType } from 'interface/push/push.interface';
import { getDefaultLanguage } from 'helpers/templated-experience.helper';
import { getContentListItems, getPrimaryContent } from 'store/content/content.selector';
import { selectStepRule } from 'store/rule/rule.selector';
import { getPushPayloadType } from 'store/push/push.selector';
import { RootState } from 'store/store';
import { PushStateItem } from 'store/push/push.type';
import { ContentDraft, PushDraft, PushSidePanelDraft, RuleDraft } from './pushDraft.types';

export function createEmptyDraft(): PushSidePanelDraft {
  return {
    push: {},
    rule: {},
    content: {},
  };
}

export function seedDraftFromState(state: RootState, stepIdx: number): PushSidePanelDraft {
  const stepPushRefId = state.te.journey.steps[stepIdx]?.push;
  const push = stepPushRefId ? state.te.push.byRefId[stepPushRefId] : undefined;
  const rule = selectStepRule(state.te, stepIdx);
  const pushPayloadType =
    push?.pushPayloadType ?? getPushPayloadType(state, push?.refId ?? '')?.type ?? ('basic' as PushPayloadType);

  const pushDraft: PushDraft = push
    ? {
        ...structuredClone(push),
        pushPayloadType,
      }
    : {
        pushPayloadType,
      };

  const ruleDraft: RuleDraft = {
    refId: rule?.refId,
    emitEveryEvaluationResult: rule?.emitEveryEvaluationResult,
  };

  const contentDraft: ContentDraft = {};

  const dependencyGraph = state.te.journey.dependencyGraph;
  const pushNode = dependencyGraph?.[stepPushRefId ?? ''];
  const explicitlyLinkedRefId = pushNode?.requires?.find((dep) => dep.var.includes('content'))?.refId;

  if (explicitlyLinkedRefId) {
    const contentStateItem = state.te.content.byRefId[explicitlyLinkedRefId];
    if (contentStateItem?.payload?.id) {
      contentDraft.selectedContentRefId = explicitlyLinkedRefId;
    }
  }

  return {
    push: pushDraft,
    rule: ruleDraft,
    content: contentDraft,
  };
}

export function buildUpdatePushFields(pushDraft: PushDraft): Partial<PushStateItem> {
  const {
    title,
    alert,
    url,
    actionLinkScheme,
    actionLinkType,
    pushPayloadType,
    pushPayloadTypeId,
    max,
    cooldown,
    delay,
    limit,
    enableOptionalNode,
  } = pushDraft;

  return {
    title,
    alert,
    url,
    actionLinkScheme,
    actionLinkType,
    pushPayloadType,
    pushPayloadTypeId,
    max,
    cooldown,
    delay,
    limit,
    enableOptionalNode: enableOptionalNode ?? true,
  };
}

export function getExistingContentRefIdForContentId(state: RootState, stepIdx: number, contentId: string) {
  const contentListItems = getContentListItems(state, stepIdx);
  return contentListItems.find((c) => c.id === contentId)?.refId;
}

export function getLinkedContentRefIdForPush(state: RootState, pushRefId: string) {
  return state.te.journey.dependencyGraph?.[pushRefId]?.requires?.find((dep) => dep.var.includes('content'))
    ?.refId;
}

export function getPrimaryContentRefId(state: RootState, stepIdx: number) {
  return getPrimaryContent(state.te, stepIdx);
}

export function getDefaultLangFromState(state: RootState) {
  return getDefaultLanguage(state.settings.languages);
}
